import { supabase } from '../lib/supabase';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  // Check PostgreSQL error code class (first 2 characters)
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    
    // Class 42 = Schema errors (syntax, undefined table/column, etc.)
    if (errorClass === '42') {
      return true;
    }
    
    // Class 08 = Connection errors - treat as schema/system errors
    if (errorClass === '08') {
      return true;
    }
  }
  
  // Fallback: Check error message patterns for schema issues
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Normalize error to always be a proper Error instance
function normalizeError(err, fallback) {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const msg = err?.message || fallback || 'Unknown error';
    return Object.assign(new Error(msg), err);
  }
  return new Error(err || fallback || 'Unknown error');
}

export const bidService = {
  // Get all bids
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.eq('is_archived', false)
        ?.order('created_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching bids');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching bids:', error?.message);
      throw normalizeError(error, 'Error fetching bids');
    }
  },

  // Get all archived bids
  async getArchived() {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.eq('is_archived', true)
        ?.order('updated_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          throw normalizeError(error, 'Schema error fetching archived bids');
        }
        return { data: null, error };
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching archived bids:', error?.message);
      throw normalizeError(error, 'Error fetching archived bids');
    }
  },

  // Archive a bid
  async archive(id, currentStatus) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.update({ is_archived: true, pre_archive_status: currentStatus })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw normalizeError(error, 'Error archiving bid');
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error archiving bid:', error?.message);
      return { data: null, error };
    }
  },

  // Unarchive a bid
  async unarchive(id) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.update({ is_archived: false, pre_archive_status: null })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw normalizeError(error, 'Error unarchiving bid');
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error unarchiving bid:', error?.message);
      return { data: null, error };
    }
  },

  // Get single bid by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching bid');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching bid:', error?.message);
      throw normalizeError(error, 'Error fetching bid');
    }
  },

  // Create new bid
  async create(bidData) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const bidToInsert = toSnakeCase({
        ...bidData,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('bids')
        ?.insert(bidToInsert)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error creating bid');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating bid:', error?.message);
      throw normalizeError(error, 'Error creating bid');
    }
  },

  // Update bid
  async update(id, bidData) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const bidToUpdate = toSnakeCase(bidData);

      const { data, error } = await supabase
        ?.from('bids')
        ?.update(bidToUpdate)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error updating bid');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating bid:', error?.message);
      throw normalizeError(error, 'Error updating bid');
    }
  },

  // Delete bid
  async delete(id) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        ?.from('bids')
        ?.delete()
        ?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error deleting bid');
        } else {
          console.log('Data error:', error?.message);
          return { error };
        }
      }
      return { error: null };
    } catch (error) {
      console.error('Error deleting bid:', error?.message);
      throw normalizeError(error, 'Error deleting bid');
    }
  },

  // Get bids by status
  async getByStatus(status) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.eq('status', status)
        ?.order('created_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching bids by status');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching bids by status:', error?.message);
      throw normalizeError(error, 'Error fetching bids by status');
    }
  },

  // Search bids by customer name, project name, or bid number
  async search(searchTerm) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.or(`customer_name.ilike.%${searchTerm}%,project_name.ilike.%${searchTerm}%,bid_number.ilike.%${searchTerm}%,project_location.ilike.%${searchTerm}%`)
        ?.order('created_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error searching bids');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error searching bids:', error?.message);
      throw normalizeError(error, 'Error searching bids');
    }
  },

  // Get bids by customer name
  async getByCustomer(customerName) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.ilike('customer_name', customerName)
        ?.order('created_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching bids by customer');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching bids by customer:', error?.message);
      throw normalizeError(error, 'Error fetching bids by customer');
    }
  },

  // Advanced search with multiple filters
  async advancedSearch(filters = {}) {
    try {
      let query = supabase?.from('bids')?.select('*');

      // Search term - searches across customer_name, project_name, bid_number, project_location
      if (filters?.searchTerm) {
        const term = filters?.searchTerm;
        query = query?.or(
          `customer_name.ilike.%${term}%,project_name.ilike.%${term}%,bid_number.ilike.%${term}%,project_location.ilike.%${term}%`
        );
      }

      // Filter by customer name
      if (filters?.customerName) {
        query = query?.ilike('customer_name', `%${filters?.customerName}%`);
      }

      // Filter by status
      if (filters?.status && filters?.status?.length > 0) {
        if (Array.isArray(filters?.status)) {
          query = query?.in('status', filters?.status);
        } else {
          query = query?.eq('status', filters?.status);
        }
      }

      // Filter by amount range
      if (filters?.minAmount !== undefined && filters?.minAmount !== null) {
        query = query?.gte('total_amount', filters?.minAmount);
      }

      if (filters?.maxAmount !== undefined && filters?.maxAmount !== null) {
        query = query?.lte('total_amount', filters?.maxAmount);
      }

      // Filter by date range (created_date)
      if (filters?.startDate) {
        query = query?.gte('created_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_date', filters?.endDate);
      }

      // Filter by due date range
      if (filters?.startDueDate) {
        query = query?.gte('due_date', filters?.startDueDate);
      }

      if (filters?.endDueDate) {
        query = query?.lte('due_date', filters?.endDueDate);
      }

      // Sorting
      const sortBy = filters?.sortBy || 'created_date';
      const sortOrder = filters?.sortOrder === 'asc' ? true : false;
      query = query?.order(sortBy, { ascending: sortOrder });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error in advanced search');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error in advanced search:', error?.message);
      throw normalizeError(error, 'Error in advanced search');
    }
  },

  // Filter bids with comprehensive options
  async filter(filters = {}) {
    try {
      let query = supabase?.from('bids')?.select('*');

      // Search term
      if (filters?.searchTerm) {
        const term = filters?.searchTerm;
        query = query?.or(
          `customer_name.ilike.%${term}%,project_name.ilike.%${term}%,bid_number.ilike.%${term}%,project_location.ilike.%${term}%`
        );
      }

      // Filter by customer name
      if (filters?.customerName) {
        query = query?.ilike('customer_name', `%${filters?.customerName}%`);
      }

      // Filter by status
      if (filters?.status && filters?.status?.length > 0) {
        if (Array.isArray(filters?.status)) {
          query = query?.in('status', filters?.status);
        } else {
          query = query?.eq('status', filters?.status);
        }
      }

      // Filter by amount range
      if (filters?.minAmount !== undefined && filters?.minAmount !== null) {
        query = query?.gte('total_amount', filters?.minAmount);
      }

      if (filters?.maxAmount !== undefined && filters?.maxAmount !== null) {
        query = query?.lte('total_amount', filters?.maxAmount);
      }

      // Filter by date range
      if (filters?.startDate) {
        query = query?.gte('created_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_date', filters?.endDate);
      }

      // Sorting
      const sortBy = filters?.sortBy || 'created_date';
      const sortOrder = filters?.sortOrder === 'asc' ? true : false;
      query = query?.order(sortBy, { ascending: sortOrder });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error filtering bids');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error filtering bids:', error?.message);
      throw normalizeError(error, 'Error filtering bids');
    }
  },

  // Get bid statistics for a specific customer
  async getCustomerBidStats(customerName) {
    try {
      const { data: bids, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.ilike('customer_name', customerName);

      if (error) {
        if (isSchemaError(error)) {
          throw normalizeError(error, 'Schema error fetching customer bid stats');
        }
        return { data: null, error };
      }

      // Calculate statistics
      const stats = {
        totalBids: bids?.length || 0,
        pendingBids: bids?.filter(b => b?.status === 'Pending')?.length || 0,
        acceptedBids: bids?.filter(b => 
          b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
        )?.length || 0,
        rejectedBids: bids?.filter(b => b?.status === 'Rejected')?.length || 0,
        totalBidValue: bids?.reduce((sum, bid) => sum + (parseFloat(bid?.total_amount) || 0), 0),
        averageBidValue: bids?.length > 0 
          ? bids?.reduce((sum, bid) => sum + (parseFloat(bid?.total_amount) || 0), 0) / bids?.length
          : 0,
        conversionRate: bids?.length > 0
          ? Math.round((bids?.filter(b => 
              b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
            )?.length / bids?.length) * 100)
          : 0,
        recentBids: toCamelCase(bids?.slice(0, 5)) || []
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching customer bid stats:', error?.message);
      throw normalizeError(error, 'Error fetching customer bid stats');
    }
  },

  // Get overall bid statistics
  async getOverallStats() {
    try {
      const { data: bids, error } = await supabase
        ?.from('bids')
        ?.select('*');

      if (error) {
        if (isSchemaError(error)) {
          throw normalizeError(error, 'Schema error fetching overall bid stats');
        }
        return { data: null, error };
      }

      // Calculate overall statistics
      const stats = {
        totalBids: bids?.length || 0,
        pendingBids: bids?.filter(b => b?.status === 'Pending')?.length || 0,
        acceptedBids: bids?.filter(b => 
          b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
        )?.length || 0,
        rejectedBids: bids?.filter(b => b?.status === 'Rejected')?.length || 0,
        totalBidValue: bids?.reduce((sum, bid) => sum + (parseFloat(bid?.total_amount) || 0), 0),
        averageBidValue: bids?.length > 0 
          ? bids?.reduce((sum, bid) => sum + (parseFloat(bid?.total_amount) || 0), 0) / bids?.length
          : 0,
        conversionRate: bids?.length > 0
          ? Math.round((bids?.filter(b => 
              b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
            )?.length / bids?.length) * 100)
          : 0
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching overall bid stats:', error?.message);
      throw normalizeError(error, 'Error fetching overall bid stats');
    }
  },

  // Subscribe to real-time bid changes
  subscribeToChanges(onInsert, onUpdate, onDelete) {
    const channel = supabase?.channel('bids_realtime')
      ?.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bids' },
        (payload) => onInsert && onInsert(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'bids' },
        (payload) => onUpdate && onUpdate(toCamelCase(payload?.new))
      )
      ?.on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'bids' },
        (payload) => onDelete && onDelete(payload?.old?.id)
      )
      ?.subscribe();

    return channel;
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  },
};